/**
 * devDocsUrl.ts — адрес документации для разработчика на сайте
 * docs.subster.ai, на языке кабинета.
 *
 * Решение владельца 05.10.2026 (WEB-2708, «сразу нормально делаем, разрываем
 * связь со старой докой»): все ссылки кабинета — «Документация», «Справочник»,
 * «Как подключить» MCP — ведут на новый сайт документации. Старую статичную
 * страницу `dev-docs.html` / `dev-docs.en.html` кабинет больше не открывает.
 * Язык страницы — язык, выбранный человеком в сервисе (`users.language`, хук
 * `useUiLocale`; решение владельца 15.09.2026): открыть англоязычному клиенту
 * русскую страницу — тот же дефект, что русская строка в `.tsx`.
 *
 * АДРЕСА САЙТА (`docs-site/README.md`, «Адреса страниц»): `/<язык>/<раздел>/<слаг>/`,
 * пути у языков одинаковые, отличается только первый сегмент. Поэтому
 * страница задаётся одним путём на оба языка. Якорей у ссылок кабинета нет:
 * якоря сайта строятся из заголовков и у языков РАЗНЫЕ (`#перед-началом` /
 * `#before-you-start`); бывший якорь `#mcp` старой страницы стал отдельной
 * статьёй `developer/mcp/`.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ. Адрес раньше жил константой в ЧЕТЫРЁХ местах сразу —
 * классический «правило довели до одного конца из двух». Место одно, а второй
 * конец правила стережёт `devDocsUrl.guard.test.ts`: (1) обходом исходников
 * ищет литерал с адресом сайта или старой страницы мимо этого помощника;
 * (2) собирает сайт настоящим сборщиком и проверяет, что каждая страница из
 * `DOCS_SITE_PAGES` есть на нём статьёй в обоих языках.
 */
import type { UiLocale } from '@/i18n';

/** Сайт документации (сервис docs-site на Railway, поддомен — решение владельца 05.10). */
export const DOCS_SITE_ORIGIN = 'https://docs.subster.ai';

/**
 * Страницы сайта, на которые ведёт кабинет: путь от `/<язык>/`.
 *   · `developer` — вход дорожки «Разработчику» («Обзор: три способа
 *     подключиться»), сюда ведут «Документация для разработчика» и «Полная
 *     документация»;
 *   · `mcp` — подключение MCP-сервера к ИИ-помощнику («Как подключить»).
 */
export const DOCS_SITE_PAGES = {
  developer: 'developer/overview/',
  mcp: 'developer/mcp/',
} as const;

export type DocsSitePage = keyof typeof DOCS_SITE_PAGES;

/**
 * Адрес страницы сайта для языка кабинета.
 *
 * @param locale язык кабинета (`useUiLocale()` в компоненте, `getUiLocale()` вне React).
 * @param page страница из `DOCS_SITE_PAGES`; по умолчанию — вход дорожки «Разработчику».
 *
 * Умолчание языка — АНГЛИЙСКИЙ, а не русский: `DEFAULT_UI_LOCALE` в
 * `i18n/locale.ts` равен `en`, и это одно умолчание на весь продукт (колонка
 * базы, `auth/utils/resolve-language.ts`, плеер). Проверка «именно ru» вместо
 * «именно en» держит это умолчание честно даже для языка, которого мы не
 * поддерживаем: он попадёт на английскую страницу, а не на русскую.
 */
export function devDocsUrl(
  locale: UiLocale,
  page: DocsSitePage = 'developer',
): string {
  const lang = locale === 'ru' ? 'ru' : 'en';
  return `${DOCS_SITE_ORIGIN}/${lang}/${DOCS_SITE_PAGES[page]}`;
}

/**
 * Главная сайта документации для языка кабинета — пункт «Документация» в меню
 * пользователя (просьба владельца 06.10.2026). Умолчание языка — как у
 * `devDocsUrl`.
 */
export function docsHomeUrl(locale: UiLocale): string {
  const lang = locale === 'ru' ? 'ru' : 'en';
  return `${DOCS_SITE_ORIGIN}/${lang}/`;
}
